/**
 * FeedbackButton — Feedback FAB for all Patchwise pages.
 *
 * Posts directly to UITL central intake endpoint.
 * Admin users (isPlatformAdmin) see an auto-fix toggle.
 * Public/unauthenticated users see an optional email field.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Fab, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, ToggleButtonGroup, ToggleButton,
  Typography, Box, Alert, Stack,
  FormControlLabel, Switch,
} from '@mui/material';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useAuth } from '../contexts/AuthContext';

const UITL_INTAKE_URL = (process.env.REACT_APP_UITL_API_URL || 'https://app.uitl.ai') + '/api/v1/feedback/intake';
const VENTURE = 'Patchwise';

const FeedbackButton: React.FC = () => {
  const location = useLocation();
  const { user, isPlatformAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<string>('feature');
  const [autoFix, setAutoFix] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [trackingUrl, setTrackingUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navHistoryRef = useRef<string[]>([]);

  useEffect(() => {
    navHistoryRef.current = [
      location.pathname,
      ...navHistoryRef.current.filter((p) => p !== location.pathname),
    ].slice(0, 5);
  }, [location.pathname]);

  const handleOpen = () => {
    setOpen(true);
    setType('feature');
    setTitle('');
    setDescription('');
    setEmail('');
    setAutoFix(false);
    setSubmitted(false);
    setTrackingUrl(null);
    setError(null);
  };

  const handleClose = () => {
    if (!submitting) setOpen(false);
  };

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const resp = await fetch(UITL_INTAKE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          venture: VENTURE,
          type,
          title: title.trim(),
          description: description.trim() || null,
          page: location.pathname,
          source_user_email: user?.email || email.trim() || null,
          auto_fix: autoFix,
          user_context: {
            navigation_history: navHistoryRef.current,
            browser_info: navigator.userAgent,
            window_size: `${window.innerWidth}x${window.innerHeight}`,
          },
        }),
      });
      if (!resp.ok) throw new Error(`Failed: ${resp.status}`);
      const result = await resp.json();
      setSubmitted(true);
      if (result.tracking_url) setTrackingUrl(result.tracking_url);
    } catch (e: any) {
      setError(e.message || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Fab
        aria-label="Submit feedback"
        onClick={handleOpen}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1300,
          bgcolor: '#10B981',
          color: '#fff',
          '&:hover': { bgcolor: '#059669' },
        }}
      >
        <FeedbackOutlinedIcon />
      </Fab>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        {submitted ? (
          <>
            <DialogTitle sx={{ fontWeight: 600, textAlign: 'center', pt: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1, display: 'block', mx: 'auto' }} />
              {autoFix ? 'Queued for auto-fix!' : 'Thank you!'}
            </DialogTitle>
            <DialogContent sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {autoFix
                  ? 'Patch McFixitface will analyze and fix this autonomously.'
                  : 'Your feedback has been received. We review every submission.'}
              </Typography>
              {trackingUrl && (
                <Box sx={{ backgroundColor: 'action.hover', borderRadius: 2, p: 2, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Track your feedback's progress:
                  </Typography>
                  <Button
                    href={trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="text"
                    sx={{ fontWeight: 600 }}
                  >
                    View Status
                  </Button>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle sx={{ fontWeight: 600 }}>Send Feedback</DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2, mt: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  What type of feedback?
                </Typography>
                <ToggleButtonGroup
                  value={type}
                  exclusive
                  onChange={(_, val) => { if (val) setType(val); }}
                  size="small"
                >
                  <ToggleButton value="bug" sx={{ textTransform: 'none', gap: 0.5 }}>
                    <BugReportIcon fontSize="small" /> Bug
                  </ToggleButton>
                  <ToggleButton value="feature" sx={{ textTransform: 'none', gap: 0.5 }}>
                    <LightbulbIcon fontSize="small" /> Feature
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <TextField
                label="Title"
                fullWidth
                required
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, 255))}
                helperText={`${title.length}/255`}
                inputProps={{ maxLength: 255 }}
                sx={{ mb: 2 }}
                autoFocus
              />

              <TextField
                label={type === 'bug' ? 'What happened?' : 'Additional details (optional)'}
                fullWidth
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={type === 'bug' ? 'Steps to reproduce, what you expected...' : 'Any other details...'}
                sx={{ mb: 2 }}
              />

              {!user && (
                <TextField
                  label="Email (optional — for status updates)"
                  fullWidth
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  helperText="We'll email you when your feedback is addressed"
                />
              )}

              {isPlatformAdmin && (
                <FormControlLabel
                  sx={{ mt: 2 }}
                  control={
                    <Switch
                      checked={autoFix}
                      onChange={(e) => setAutoFix(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <AutoFixHighIcon sx={{ fontSize: 16, color: autoFix ? 'primary.main' : 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontWeight: autoFix ? 600 : 400 }}>
                        Auto-fix with Patch McFixitface
                      </Typography>
                    </Stack>
                  }
                />
              )}

              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!title.trim() || submitting}
              >
                {submitting ? 'Sending...' : 'Submit'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default FeedbackButton;
